import React, { useState, useRef, useEffect } from 'react';
import ReactDOMServer from 'react-dom/server';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Printer, Loader2, Monitor, Tag, Settings, ChevronDown, Download, FileText } from 'lucide-react';
import JsBarcode from 'jsbarcode';
import { QRCodeSVG } from 'qrcode.react';
import { jsPDF } from 'jspdf';
import { itemCodesAPI } from '../lib/api';
import { toast } from 'sonner';

// Label size configurations with physical dimensions in inches
const LABEL_SIZES = {
  '4x6': {
    name: '4×6 in (Shipping Label)',
    width: 4,      // inches
    height: 6,     // inches
    widthPx: 288,  // 4in * 72dpi for PDF
    heightPx: 432, // 6in * 72dpi for PDF
    barcode: { width: 2.5, height: 80, fontSize: 14 },
    qr: { sizeIn: 1.25, minSizeIn: 1.0 },  // QR size in inches
    fontSize: { name: 18, sku: 14, code: 12 },
    padding: 0.25, // inches
  },
  '2.25x1.25': {
    name: '2.25×1.25 in (Small Asset Label)',
    width: 2.25,   // inches
    height: 1.25,  // inches
    widthPx: 162,  // 2.25in * 72dpi for PDF
    heightPx: 90,  // 1.25in * 72dpi for PDF
    barcode: { width: 1.0, height: 25, fontSize: 7 },
    qr: { sizeIn: 0.7, minSizeIn: 0.6 },   // QR size in inches
    fontSize: { name: 9, sku: 7, code: 6 },
    padding: 0.05, // inches
  },
};

// Scale options for printer calibration
const SCALE_OPTIONS = [
  { value: '0.90', label: '90%' },
  { value: '0.95', label: '95%' },
  { value: '1.00', label: '100%' },
  { value: '1.05', label: '105%' },
  { value: '1.10', label: '110%' },
];

// Standard mode QR size
const STANDARD_QR_SIZE = 100;

/**
 * Generate QR code as SVG string using qrcode.react's QRCodeSVG
 * SVG renders reliably in print preview (unlike Canvas)
 */
const generateQRCodeSVG = (value, size) => {
  const svgElement = (
    <QRCodeSVG 
      value={value} 
      size={size} 
      level="M"
      includeMargin={true}
      bgColor="#ffffff"
      fgColor="#000000"
    />
  );
  return ReactDOMServer.renderToStaticMarkup(svgElement);
};

/**
 * Generate barcode as SVG string for PDF embedding
 */
const generateBarcodeSVG = (code, options = {}) => {
  const { width = 2, height = 50, fontSize = 12 } = options;
  
  // Create a temporary SVG element
  const tempSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  document.body.appendChild(tempSvg);
  
  try {
    JsBarcode(tempSvg, code, {
      format: 'CODE128',
      width,
      height,
      displayValue: true,
      fontSize,
      margin: 5,
      background: '#ffffff',
      lineColor: '#000000',
    });
    
    const svgString = new XMLSerializer().serializeToString(tempSvg);
    return svgString;
  } catch (e) {
    console.error('Barcode generation error:', e);
    return null;
  } finally {
    document.body.removeChild(tempSvg);
  }
};

const BarcodeLabel = ({ code, name, sku, type = 'barcode' }) => {
  const barcodeRef = useRef(null);
  
  useEffect(() => {
    if (type === 'barcode' && barcodeRef.current && code) {
      try {
        JsBarcode(barcodeRef.current, code, {
          format: 'CODE128',
          width: 2,
          height: 50,
          displayValue: true,
          fontSize: 12,
          margin: 5,
        });
      } catch (err) {
        console.error('Barcode generation error:', err);
      }
    }
  }, [code, type]);

  return (
    <div className="label-item border border-gray-300 p-4 bg-white text-center" style={{ width: '250px', pageBreakInside: 'avoid' }}>
      <div className="font-bold text-sm mb-1 truncate">{name}</div>
      {sku && <div className="text-xs text-gray-600 mb-2">SKU: {sku}</div>}
      {type === 'barcode' ? (
        <div className="preview-barcode w-full flex justify-center overflow-hidden">
          <svg 
            ref={barcodeRef} 
            className="mx-auto"
            style={{ 
              maxWidth: '100%', 
              height: 'auto',
              transform: 'scale(0.9)',
              transformOrigin: 'center center'
            }} 
          />
        </div>
      ) : (
        <div className="flex flex-col items-center">
          <QRCodeSVG value={code} size={100} level="M" includeMargin={true} />
          <div className="text-xs mt-1 font-mono">{code}</div>
        </div>
      )}
    </div>
  );
};

export const LabelPrinter = ({ 
  open, 
  onClose, 
  itemId,
  itemName,
  itemSku
}) => {
  const [loading, setLoading] = useState(false);
  const [labelData, setLabelData] = useState(null);
  const [labelType, setLabelType] = useState('barcode');
  const [quantity, setQuantity] = useState('1');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  
  // Print mode with localStorage persistence
  const [printMode, setPrintMode] = useState(() => {
    return localStorage.getItem('labelPrinter_printMode') || 'standard';
  });
  const [labelSize, setLabelSize] = useState(() => {
    return localStorage.getItem('labelPrinter_labelSize') || '4x6';
  });
  
  // Printer calibration settings
  const [printScale, setPrintScale] = useState(() => {
    return localStorage.getItem('labelPrinter_scale') || '1.00';
  });
  const [offsetX, setOffsetX] = useState(() => {
    return parseFloat(localStorage.getItem('labelPrinter_offsetX')) || 0;
  });
  const [offsetY, setOffsetY] = useState(() => {
    return parseFloat(localStorage.getItem('labelPrinter_offsetY')) || 0;
  });

  // Persist all settings to localStorage
  useEffect(() => {
    localStorage.setItem('labelPrinter_printMode', printMode);
  }, [printMode]);

  useEffect(() => {
    localStorage.setItem('labelPrinter_labelSize', labelSize);
  }, [labelSize]);

  useEffect(() => {
    localStorage.setItem('labelPrinter_scale', printScale);
  }, [printScale]);

  useEffect(() => {
    localStorage.setItem('labelPrinter_offsetX', offsetX.toString());
  }, [offsetX]);

  useEffect(() => {
    localStorage.setItem('labelPrinter_offsetY', offsetY.toString());
  }, [offsetY]);

  useEffect(() => {
    if (open && itemId) {
      loadLabelData();
    }
  }, [open, itemId]);

  const loadLabelData = async () => {
    setLoading(true);
    try {
      const res = await itemCodesAPI.getLabel(itemId);
      setLabelData(res.data);
    } catch (err) {
      toast.error('Failed to load label data');
    } finally {
      setLoading(false);
    }
  };

  const handleOffsetChange = (axis, value) => {
    const numValue = parseFloat(value) || 0;
    const clampedValue = Math.max(-0.25, Math.min(0.25, numValue));
    if (axis === 'x') {
      setOffsetX(clampedValue);
    } else {
      setOffsetY(clampedValue);
    }
  };

  const handlePrint = () => {
    const qty = parseInt(quantity) || 1;
    if (qty < 1 || qty > 100) {
      toast.error('Please enter a quantity between 1 and 100');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) {
      toast.error('Please allow pop-ups to print labels');
      return;
    }

    const code = labelData?.primary_code_value || `${itemId.slice(0, 8)}`;
    const displayName = itemName || labelData?.item_name;
    const displaySku = itemSku || labelData?.sku;
    
    const sizeConfig = LABEL_SIZES[labelSize];
    const isLabelPrinter = printMode === 'label';
    
    // Calculate QR size in pixels (72 DPI for screen/print)
    const qrSizeInPx = isLabelPrinter 
      ? Math.round(sizeConfig.qr.sizeIn * 72) 
      : STANDARD_QR_SIZE;
    
    // Pre-generate QR code SVG string
    const qrSvgString = labelType === 'qr' ? generateQRCodeSVG(code, qrSizeInPx) : '';
    
    // Generate labels HTML
    let labelsHtml = '';
    for (let i = 0; i < qty; i++) {
      if (labelType === 'barcode') {
        labelsHtml += `
          <div class="label${isLabelPrinter ? ' label-printer-item' : ''}">
            <div class="label-content">
              <div class="name">${displayName}</div>
              ${displaySku ? `<div class="sku">SKU: ${displaySku}</div>` : ''}
              <div class="barcode-container">
                <svg id="barcode-${i}"></svg>
              </div>
            </div>
          </div>
        `;
      } else {
        labelsHtml += `
          <div class="label${isLabelPrinter ? ' label-printer-item' : ''}">
            <div class="label-content">
              <div class="name">${displayName}</div>
              ${displaySku ? `<div class="sku">SKU: ${displaySku}</div>` : ''}
              <div class="qr-container">
                ${qrSvgString}
              </div>
              <div class="code-text">${code}</div>
            </div>
          </div>
        `;
      }
    }

    // Build CSS based on print mode
    const standardStyles = `
      @page { size: letter; margin: 10mm; }
      .labels-container {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        justify-content: flex-start;
        align-content: flex-start;
      }
      .label {
        border: 1px solid #999;
        padding: 12px 10px;
        text-align: center;
        width: 250px;
        min-height: 120px;
        page-break-inside: avoid;
        break-inside: avoid;
        background: white;
      }
      .label-content { width: 100%; }
      .name {
        font-weight: bold;
        font-size: 14px;
        margin-bottom: 4px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .sku { font-size: 11px; color: #555; margin-bottom: 8px; }
      .barcode-container {
        width: 100%;
        min-height: 60px;
        display: flex;
        justify-content: center;
        align-items: center;
      }
      .barcode-container svg { max-width: 220px; width: 100%; height: auto; }
      .qr-container {
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 8px auto;
      }
      .qr-container svg { width: ${STANDARD_QR_SIZE}px; height: ${STANDARD_QR_SIZE}px; }
      .code-text { font-family: 'Courier New', monospace; font-size: 10px; margin-top: 4px; }
      @media print {
        html, body { margin: 0; padding: 5mm; background: white; }
        .label { border: 1px solid #666; }
      }
    `;

    // Label printer mode with physical units (inches)
    const scale = parseFloat(printScale);
    const labelPrinterStyles = `
      @page {
        size: ${sizeConfig.width}in ${sizeConfig.height}in;
        margin: 0;
      }
      html, body { margin: 0; padding: 0; }
      .labels-container { display: block; }
      .label-printer-item {
        width: ${sizeConfig.width}in;
        height: ${sizeConfig.height}in;
        padding: ${sizeConfig.padding}in;
        margin: 0;
        border: none;
        background: white;
        display: flex;
        justify-content: center;
        align-items: center;
        box-sizing: border-box;
        overflow: hidden;
        page-break-after: always;
        break-after: page;
      }
      .label-printer-item:last-child {
        page-break-after: auto;
        break-after: auto;
      }
      .label-content {
        transform: scale(${scale}) translate(${offsetX}in, ${offsetY}in);
        transform-origin: center center;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        width: 100%;
      }
      .name {
        font-weight: bold;
        font-size: ${sizeConfig.fontSize.name}px;
        margin-bottom: 2px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
        line-height: 1.2;
      }
      .sku {
        font-size: ${sizeConfig.fontSize.sku}px;
        color: #333;
        margin-bottom: 4px;
        line-height: 1.2;
      }
      .barcode-container {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        flex: 1;
        min-height: 0;
      }
      .barcode-container svg {
        max-width: 95%;
        height: auto;
        max-height: 100%;
      }
      .qr-container {
        display: flex;
        justify-content: center;
        align-items: center;
        flex: 1;
        min-height: 0;
      }
      .qr-container svg {
        width: ${sizeConfig.qr.sizeIn}in;
        height: ${sizeConfig.qr.sizeIn}in;
        min-width: ${sizeConfig.qr.minSizeIn}in;
        min-height: ${sizeConfig.qr.minSizeIn}in;
      }
      .code-text {
        font-family: 'Courier New', monospace;
        font-size: ${sizeConfig.fontSize.code}px;
        margin-top: 2px;
        line-height: 1.2;
      }
      @media print {
        html, body { margin: 0; padding: 0; background: white; }
        .label-printer-item { border: none; box-shadow: none; }
      }
      @media screen {
        body { padding: 20px; background: #f0f0f0; }
        .label-printer-item { 
          border: 1px dashed #999; 
          margin-bottom: 20px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
      }
    `;

    const styles = isLabelPrinter ? labelPrinterStyles : standardStyles;
    
    // Barcode config based on mode
    const barcodeConfig = isLabelPrinter 
      ? { width: sizeConfig.barcode.width, height: sizeConfig.barcode.height, fontSize: sizeConfig.barcode.fontSize }
      : { width: 2, height: 50, fontSize: 12 };

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print Labels - ${displayName}</title>
        <style>
          * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          html, body {
            font-family: Arial, Helvetica, sans-serif;
            font-size: 12px;
            line-height: 1.3;
          }
          ${styles}
        </style>
        ${labelType === 'barcode' ? '<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.12.1/dist/JsBarcode.all.min.js"><\/script>' : ''}
      </head>
      <body>
        <div class="labels-container">
          ${labelsHtml}
        </div>
        ${labelType === 'barcode' ? `
        <script>
          const code = "${code}";
          const qty = ${qty};
          const barcodeConfig = ${JSON.stringify(barcodeConfig)};
          
          for (let i = 0; i < qty; i++) {
            try {
              JsBarcode("#barcode-" + i, code, {
                format: "CODE128",
                width: barcodeConfig.width,
                height: barcodeConfig.height,
                displayValue: true,
                fontSize: barcodeConfig.fontSize,
                margin: 3
              });
            } catch (e) { console.error(e); }
          }
          
          setTimeout(function() { window.print(); }, 300);
        <\/script>
        ` : `
        <script>
          setTimeout(function() { window.print(); }, 100);
        <\/script>
        `}
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleDownloadPDF = async () => {
    const qty = parseInt(quantity) || 1;
    if (qty < 1 || qty > 100) {
      toast.error('Please enter a quantity between 1 and 100');
      return;
    }

    setPdfGenerating(true);
    
    try {
      const code = labelData?.primary_code_value || `${itemId.slice(0, 8)}`;
      const displayName = itemName || labelData?.item_name;
      const displaySku = itemSku || labelData?.sku;
      
      const sizeConfig = LABEL_SIZES[labelSize];
      const scale = parseFloat(printScale);
      
      // Create PDF with exact label dimensions (in inches, converted to points: 1in = 72pt)
      const pdfWidth = sizeConfig.width * 72;
      const pdfHeight = sizeConfig.height * 72;
      
      const pdf = new jsPDF({
        orientation: sizeConfig.width > sizeConfig.height ? 'landscape' : 'portrait',
        unit: 'pt',
        format: [pdfWidth, pdfHeight],
      });

      // Calculate positions with scale and offset
      const centerX = pdfWidth / 2 + (offsetX * 72);
      const centerY = pdfHeight / 2 + (offsetY * 72);
      const padding = sizeConfig.padding * 72;

      for (let i = 0; i < qty; i++) {
        if (i > 0) {
          pdf.addPage([pdfWidth, pdfHeight]);
        }

        // Set up text properties with scaling
        const baseNameSize = sizeConfig.fontSize.name * scale;
        const baseSkuSize = sizeConfig.fontSize.sku * scale;
        const baseCodeSize = sizeConfig.fontSize.code * scale;

        let yPos = padding + 20;

        // Item name
        pdf.setFontSize(baseNameSize);
        pdf.setFont('helvetica', 'bold');
        const nameWidth = pdf.getStringUnitWidth(displayName) * baseNameSize / pdf.internal.scaleFactor;
        const truncatedName = nameWidth > (pdfWidth - padding * 2) 
          ? displayName.substring(0, Math.floor(displayName.length * (pdfWidth - padding * 2) / nameWidth)) + '...'
          : displayName;
        pdf.text(truncatedName, centerX, yPos, { align: 'center' });
        yPos += baseNameSize + 4;

        // SKU
        if (displaySku) {
          pdf.setFontSize(baseSkuSize);
          pdf.setFont('helvetica', 'normal');
          pdf.text(`SKU: ${displaySku}`, centerX, yPos, { align: 'center' });
          yPos += baseSkuSize + 8;
        }

        // Code (barcode or QR)
        const codeAreaTop = yPos;
        const codeAreaBottom = pdfHeight - padding - baseCodeSize - 10;
        const codeAreaHeight = codeAreaBottom - codeAreaTop;
        const codeAreaCenterY = codeAreaTop + codeAreaHeight / 2;

        if (labelType === 'barcode') {
          // Generate barcode SVG and embed in PDF
          const barcodeSvg = generateBarcodeSVG(code, {
            width: sizeConfig.barcode.width * scale,
            height: sizeConfig.barcode.height * scale,
            fontSize: sizeConfig.barcode.fontSize * scale,
          });
          
          if (barcodeSvg) {
            // Create temporary container and convert SVG to image
            const container = document.createElement('div');
            container.innerHTML = barcodeSvg;
            const svgEl = container.querySelector('svg');
            
            if (svgEl) {
              const svgData = new XMLSerializer().serializeToString(svgEl);
              const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
              const url = URL.createObjectURL(svgBlob);
              
              const img = new Image();
              await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = url;
              });
              
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              const scaleFactor = 2; // Higher resolution
              canvas.width = img.width * scaleFactor;
              canvas.height = img.height * scaleFactor;
              ctx.fillStyle = '#ffffff';
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              ctx.scale(scaleFactor, scaleFactor);
              ctx.drawImage(img, 0, 0);
              
              const imgData = canvas.toDataURL('image/png');
              const imgWidth = Math.min(img.width * 0.8, pdfWidth - padding * 2);
              const imgHeight = (img.height / img.width) * imgWidth;
              
              pdf.addImage(
                imgData, 
                'PNG', 
                centerX - imgWidth / 2, 
                codeAreaCenterY - imgHeight / 2,
                imgWidth, 
                imgHeight
              );
              
              URL.revokeObjectURL(url);
            }
          }
        } else {
          // QR Code - generate as SVG and convert to image
          const qrSizeInPt = sizeConfig.qr.sizeIn * 72 * scale;
          const qrSizePx = Math.round(qrSizeInPt * 2); // Higher resolution for PDF
          
          // Create QR SVG element directly
          const qrContainer = document.createElement('div');
          qrContainer.innerHTML = generateQRCodeSVG(code, qrSizePx);
          const qrSvg = qrContainer.querySelector('svg');
          
          if (qrSvg) {
            const svgData = new XMLSerializer().serializeToString(qrSvg);
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);
            
            const img = new Image();
            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
              img.src = url;
            });
            
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = qrSizePx;
            canvas.height = qrSizePx;
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, qrSizePx, qrSizePx);
            
            const imgData = canvas.toDataURL('image/png');
            
            pdf.addImage(
              imgData, 
              'PNG', 
              centerX - qrSizeInPt / 2, 
              codeAreaCenterY - qrSizeInPt / 2,
              qrSizeInPt, 
              qrSizeInPt
            );
            
            URL.revokeObjectURL(url);
          }
          
          // Code text below QR
          pdf.setFontSize(baseCodeSize);
          pdf.setFont('courier', 'normal');
          pdf.text(code, centerX, codeAreaCenterY + qrSizeInPt / 2 + baseCodeSize, { align: 'center' });
        }
      }

      // Download PDF
      const fileName = `labels_${displayName.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
      pdf.save(fileName);
      toast.success(`Downloaded ${qty} label${qty > 1 ? 's' : ''} as PDF`);
      
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF. Please try browser print instead.');
    } finally {
      setPdfGenerating(false);
    }
  };

  if (!open) return null;

  const isLabelPrinter = printMode === 'label';

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="w-5 h-5" />
            Print Labels
          </DialogTitle>
          <DialogDescription>
            Generate and print barcode or QR code labels for {itemName || labelData?.item_name}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Preview */}
            <div className="border rounded-lg p-4 bg-muted/30">
              <Label className="text-xs text-muted-foreground mb-2 block">Preview</Label>
              <div className="flex justify-center">
                <BarcodeLabel 
                  code={labelData?.primary_code_value || `${itemId?.slice(0, 8)}`}
                  name={itemName || labelData?.item_name}
                  sku={itemSku || labelData?.sku}
                  type={labelType}
                />
              </div>
            </div>

            {/* Print Mode */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Print Mode</Label>
              <RadioGroup value={printMode} onValueChange={setPrintMode} className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="standard" id="standard" />
                  <Label htmlFor="standard" className="cursor-pointer flex items-center gap-1.5">
                    <Monitor className="w-4 h-4" />
                    Standard (Letter 8.5×11)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="label" id="label" />
                  <Label htmlFor="label" className="cursor-pointer flex items-center gap-1.5">
                    <Tag className="w-4 h-4" />
                    Label Printer
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Label Size (only for Label Printer mode) */}
            {isLabelPrinter && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Label Size</Label>
                <Select value={labelSize} onValueChange={setLabelSize}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select label size" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(LABEL_SIZES).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        {config.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Printer Settings (only for Label Printer mode) */}
            {isLabelPrinter && (
              <Collapsible open={settingsOpen} onOpenChange={setSettingsOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between px-2 h-9 text-sm">
                    <span className="flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      Printer Settings
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${settingsOpen ? 'rotate-180' : ''}`} />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-3 pt-2 border-t mt-2">
                  {/* Print Density / Scale */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-muted-foreground">Print Density / Scale</Label>
                    <Select value={printScale} onValueChange={setPrintScale}>
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SCALE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Adjust to compensate for printer driver scaling</p>
                  </div>
                  
                  {/* Offsets */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-muted-foreground">X Offset (in)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="-0.25"
                        max="0.25"
                        value={offsetX}
                        onChange={(e) => handleOffsetChange('x', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-muted-foreground">Y Offset (in)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="-0.25"
                        max="0.25"
                        value={offsetY}
                        onChange={(e) => handleOffsetChange('y', e.target.value)}
                        className="h-9"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Fine-tune label position (-0.25 to +0.25 inches)</p>
                </CollapsibleContent>
              </Collapsible>
            )}

            {/* Label Type */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Label Type</Label>
              <RadioGroup value={labelType} onValueChange={setLabelType} className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="barcode" id="barcode" />
                  <Label htmlFor="barcode" className="cursor-pointer">Barcode (Code128)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="qr" id="qr" />
                  <Label htmlFor="qr" className="cursor-pointer">QR Code</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Quantity */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Quantity</Label>
              <Input
                type="number"
                min="1"
                max="100"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="Number of labels"
              />
              {isLabelPrinter && parseInt(quantity) > 1 && (
                <p className="text-xs text-muted-foreground">
                  Will print {quantity} pages (one label per page)
                </p>
              )}
            </div>

            {/* Code Info */}
            {labelData?.primary_code_value && (
              <div className="text-xs text-muted-foreground">
                Code: <span className="font-mono">{labelData.primary_code_value}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={onClose}>
                Cancel
              </Button>
              {isLabelPrinter && (
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={handleDownloadPDF}
                  disabled={pdfGenerating}
                >
                  {pdfGenerating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4 mr-2" />
                  )}
                  Save PDF
                </Button>
              )}
              <Button className="flex-1" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LabelPrinter;
